import { useState } from 'react';
import { Eye, EyeOff, Loader2, ChevronDown } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import Logo from './Logo';

interface LoginScreenProps {
  onLogin: () => void;
  onSignupSuccess?: () => void;
  onGuestContinue: () => void;
}

const COUNTRY_CODES = [
  { code: '+971', country: 'UAE', flag: '🇦🇪' },
  { code: '+966', country: 'Saudi Arabia', flag: '🇸🇦' },
  { code: '+974', country: 'Qatar', flag: '🇶🇦' },
  { code: '+965', country: 'Kuwait', flag: '🇰🇼' },
  { code: '+968', country: 'Oman', flag: '🇴🇲' },
  { code: '+973', country: 'Bahrain', flag: '🇧🇭' },
  { code: '+20', country: 'Egypt', flag: '🇪🇬' },
  { code: '+91', country: 'India', flag: '🇮🇳' },
  { code: '+92', country: 'Pakistan', flag: '🇵🇰' },
  { code: '+1', country: 'USA/Canada', flag: '🇺🇸' },
  { code: '+44', country: 'UK', flag: '🇬🇧' },
];

/**
 * Login & signup. Slice "Deuce login interface" rebuilt the surface
 * around the brand system:
 *
 *  - Deep Navy hero band carrying the wordmark on its native surface
 *    (so the lockup composites cleanly) plus a Lime Ace court-line
 *    divider at the bottom — the one permitted background motif.
 *  - Court White content card slides up over the navy with a 24px
 *    top radius (matches the bundle's hero-card radius).
 *  - Dubai-typeface display headline with brand tracking/leading.
 *  - Primary CTA is Tennis Green (workhorse on light surface). The
 *    secondary "Continue as guest" is a 2px Navy outline — that's
 *    the brand outline weight per the brief.
 *  - Voice tightened to the brief's plain-spoken style.
 */
export default function LoginScreen({ onLogin, onSignupSuccess, onGuestContinue }: LoginScreenProps) {
  const [isSignup, setIsSignup] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [countryCode, setCountryCode] = useState('+971');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [showCountryPicker, setShowCountryPicker] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { signIn, signUp } = useAuthStore();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isSignup) {
        const fullPhone = `${countryCode} ${phoneNumber}`;
        if (!fullName || !phoneNumber) {
          setError('Please fill in all fields');
          setLoading(false);
          return;
        }

        const result = await signUp(email, password, {
          full_name: fullName,
          phone: fullPhone,
        });

        if (result.success) {
          (onSignupSuccess ?? onLogin)();
        } else {
          setError(result.error || 'Signup failed');
        }
      } else {
        const result = await signIn(email, password);
        if (result.success) {
          onLogin();
        } else {
          setError(result.error || 'Login failed');
        }
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full min-h-screen bg-[#0F1E36] flex flex-col">
      {/* Navy hero — wordmark sits on its native surface so the
          baked-in background composites cleanly. Tagline echoes the
          product line. */}
      <div className="relative px-6 pt-10 pb-16 text-white">
        <Logo className="h-9" variant="dark" />
        <p className="mt-6 text-[10px] uppercase tracking-[0.16em] text-[#D4F04A] font-medium">
          Tennis. In one place.
        </p>
        <h1 className="mt-2 text-4xl font-bold leading-[1.05] tracking-[-0.02em]">
          {isSignup ? 'Create your account.' : 'Welcome back.'}
        </h1>
        <p className="mt-2 text-sm text-white/75 leading-relaxed">
          {isSignup
            ? 'Book courts, find coaches, and shop gear across Dubai.'
            : 'Pick up where you left off.'}
        </p>

        {/* Court-line motif divider — the one permitted background
            pattern. 1px Lime Ace, sits at the seam between hero and
            card. */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-[#D4F04A]/40" />
      </div>

      {/* Court White card slides up over navy */}
      <div className="flex-1 -mt-6 bg-[#FAFBF7] rounded-t-[24px] px-6 pt-8 pb-10">
        <form onSubmit={handleSubmit} className="space-y-4 max-w-md mx-auto">
          {error && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-xl px-4 py-3 text-sm text-destructive">
              {error}
            </div>
          )}

          {isSignup && (
            <>
              <Field label="Full name">
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="John Doe"
                  required={isSignup}
                  className={inputCls}
                />
              </Field>

              <Field label="Phone">
                <div className="flex gap-2 min-w-0">
                  <button
                    type="button"
                    onClick={() => setShowCountryPicker(!showCountryPicker)}
                    aria-haspopup="listbox"
                    aria-expanded={showCountryPicker}
                    className="h-12 px-3 rounded-xl bg-white border border-[#E7E9E2] text-sm text-[#0F1E36] hover:border-[#1A6B4F]/40 transition-colors flex items-center gap-2 shrink-0"
                  >
                    <span className="text-base leading-none">{COUNTRY_CODES.find(c => c.code === countryCode)?.flag}</span>
                    <span>{countryCode}</span>
                    <ChevronDown className="w-4 h-4 text-[#5F665B]" />
                  </button>
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value.replace(/[^0-9]/g, ''))}
                    placeholder="50 123 4567"
                    required={isSignup}
                    className={`${inputCls} min-w-0 flex-1`}
                  />
                </div>
                {showCountryPicker && (
                  <div
                    role="listbox"
                    className="mt-2 w-full bg-white border border-[#E7E9E2] rounded-xl shadow-md max-h-64 overflow-y-auto"
                  >
                    {COUNTRY_CODES.map((country) => (
                      <button
                        key={country.code}
                        type="button"
                        role="option"
                        aria-selected={countryCode === country.code}
                        onClick={() => {
                          setCountryCode(country.code);
                          setShowCountryPicker(false);
                        }}
                        className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-[#F4F5F2] transition-colors text-left"
                      >
                        <span className="text-lg leading-none">{country.flag}</span>
                        <span className="flex-1 text-sm">{country.country}</span>
                        <span className="text-sm text-[#5F665B]">{country.code}</span>
                      </button>
                    ))}
                  </div>
                )}
              </Field>
            </>
          )}

          <Field label="Email">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              required
              className={inputCls}
            />
          </Field>

          <Field
            label="Password"
            trailing={!isSignup ? (
              <button
                type="button"
                className="text-xs text-[#1A6B4F] hover:underline font-medium"
              >
                Forgot password?
              </button>
            ) : null}
          >
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete={isSignup ? 'new-password' : 'current-password'}
                required
                minLength={8}
                className={`${inputCls} pr-11`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5F665B] hover:text-[#0F1E36] transition-colors"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {isSignup && (
              <p className="mt-1.5 text-xs text-[#5F665B]">
                At least 8 characters, with one uppercase, one lowercase, and one number.
              </p>
            )}
          </Field>

          <button
            type="submit"
            disabled={loading}
            className="w-full h-12 rounded-xl bg-[#1A6B4F] hover:bg-[#14563F] active:bg-[#0F3D2E] text-white text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {isSignup ? 'Create Account' : 'Log In'}
          </button>

          {/* Secondary CTA: 2px Navy outline per the brand spec. Hover
              fills the surface with Navy and inverts the label. */}
          <button
            type="button"
            onClick={onGuestContinue}
            className="w-full h-12 rounded-xl border-2 border-[#0F1E36] text-[#0F1E36] text-sm font-medium hover:bg-[#0F1E36] hover:text-white active:bg-[#0A1628] transition-colors"
          >
            Continue as guest
          </button>
        </form>

        <div className="max-w-md mx-auto mt-6 text-center">
          <button
            type="button"
            onClick={() => { setIsSignup(!isSignup); setError(''); }}
            className="text-sm text-[#5F665B]"
          >
            {isSignup ? 'Already have an account? ' : "Don't have an account yet? "}
            <span className="text-[#1A6B4F] font-medium hover:underline">
              {isSignup ? 'Log in' : 'Sign up'}
            </span>
          </button>
        </div>

        <div className="max-w-md mx-auto mt-8 pt-6 border-t border-[#E7E9E2] text-center">
          <p className="text-xs text-[#5F665B]">Coach or club staff?</p>
          <a
            href={import.meta.env.VITE_PARTNER_URL || 'https://partner.acerentdubai.ae'}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block mt-1 text-sm font-medium text-[#1A6B4F] hover:underline"
          >
            Open the Partner app →
          </a>
        </div>
      </div>
    </div>
  );
}

const inputCls =
  'w-full h-12 px-3.5 rounded-xl bg-white border border-[#E7E9E2] text-sm text-[#0F1E36] placeholder:text-[#8B9183] focus:border-[#1A6B4F] focus:outline-none focus:ring-2 focus:ring-[#1A6B4F]/15 transition-all';

function Field({
  label, trailing, children,
}: {
  label: string;
  trailing?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-end justify-between mb-1.5">
        <label className="text-xs uppercase tracking-[0.08em] text-[#5F665B] font-medium">
          {label}
        </label>
        {trailing}
      </div>
      {children}
    </div>
  );
}
